import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Bell,
  Users,
  ShoppingBag,
  DollarSign,
  ChevronDown,
} from "lucide-react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
} from "recharts";
import {
  useGetDashboardStatsQuery,
  useGetUsersQuery,
  useGetOrdersQuery,
} from "../store/api/adminApi";

const MONTH_LABELS = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
];

function getMonWeekStart(date, weeksAgo = 0) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff - weeksAgo * 7);
  d.setHours(0, 0, 0, 0);
  return d;
}

function buildRegData(users, period) {
  const now = new Date();

  if (period === "Year") {
    const year = now.getFullYear();
    const counts = Array(12).fill(0);
    (users || []).forEach((u) => {
      const d = new Date(u.createdAt);
      if (d.getFullYear() === year) counts[d.getMonth()]++;
    });
    const maxVal = Math.max(...counts);
    const maxIdx = counts.lastIndexOf(maxVal);
    return MONTH_LABELS.map((month, i) => ({
      month,
      value: counts[i],
      highlight: i === maxIdx && counts[i] > 0,
    }));
  }

  if (period === "Month") {
    const weeks = Array(4)
      .fill(0)
      .map((_, i) => {
        const end = new Date(now);
        end.setDate(end.getDate() - (3 - i) * 7);
        const start = new Date(end);
        start.setDate(start.getDate() - 6);
        return { label: `Week ${i + 1}`, start, end };
      });
    const counts = weeks.map(() => 0);
    (users || []).forEach((u) => {
      const d = new Date(u.createdAt);
      weeks.forEach((w, i) => {
        if (d >= w.start && d <= w.end) counts[i]++;
      });
    });
    const maxVal = Math.max(...counts);
    const maxIdx = counts.lastIndexOf(maxVal);
    return weeks.map((w, i) => ({
      month: w.label,
      value: counts[i],
      highlight: i === maxIdx && counts[i] > 0,
    }));
  }

  // Week: last 7 days
  const days = Array(7)
    .fill(0)
    .map((_, i) => {
      const d = new Date(now);
      d.setDate(d.getDate() - (6 - i));
      return {
        label: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()],
        dateStr: d.toDateString(),
      };
    });
  const counts = days.map(() => 0);
  (users || []).forEach((u) => {
    const dateStr = new Date(u.createdAt).toDateString();
    days.forEach((d, i) => {
      if (d.dateStr === dateStr) counts[i]++;
    });
  });
  const maxVal = Math.max(...counts);
  const maxIdx = counts.lastIndexOf(maxVal);
  return days.map((d, i) => ({
    month: d.label,
    value: counts[i],
    highlight: i === maxIdx && counts[i] > 0,
  }));
}

function buildOrdersData(orders, period) {
  const now = new Date();

  if (period === "This week" || period === "Last week") {
    const weeksAgo = period === "Last week" ? 1 : 0;
    const weekStart = getMonWeekStart(now, weeksAgo);
    const days = Array(7)
      .fill(0)
      .map((_, i) => {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        return {
          label: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][i],
          dateStr: d.toDateString(),
        };
      });
    const counts = days.map(() => 0);
    (orders || []).forEach((o) => {
      const dateStr = new Date(o.createdAt).toDateString();
      days.forEach((d, i) => {
        if (d.dateStr === dateStr) counts[i]++;
      });
    });
    return days.map((d, i) => ({ day: d.label, value: counts[i] }));
  }

  // This month: group by week
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const weeks = [];
  let cursor = new Date(monthStart);
  let wNum = 1;
  while (cursor <= now && cursor.getMonth() === now.getMonth()) {
    const start = new Date(cursor);
    const end = new Date(cursor);
    end.setDate(end.getDate() + 6);
    weeks.push({ label: `W${wNum}`, start, end: end > now ? now : end });
    cursor.setDate(cursor.getDate() + 7);
    wNum++;
  }
  const counts = weeks.map(() => 0);
  (orders || []).forEach((o) => {
    const d = new Date(o.createdAt);
    weeks.forEach((w, i) => {
      if (d >= w.start && d <= w.end) counts[i]++;
    });
  });
  return weeks.map((w, i) => ({ day: w.label, value: counts[i] }));
}

function niceMax(val) {
  if (val <= 0) return 5;
  const step = val <= 5 ? 1 : val <= 20 ? 5 : 10;
  return Math.ceil(val / step) * step + step;
}

function niceTicks(max) {
  const count = 5;
  const step = max / count;
  return Array.from({ length: count + 1 }, (_, i) => Math.round(i * step));
}

function CustomBarShape(props) {
  const { x, y, width, height, payload } = props;
  if (payload?.highlight) {
    const grad = `grad-${payload.month}`;
    return (
      <g>
        <defs>
          <linearGradient id={grad} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#b0b0b0" stopOpacity={0.7} />
            <stop offset="100%" stopColor="#e0e0e0" stopOpacity={0.2} />
          </linearGradient>
        </defs>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={`url(#${grad})`}
          rx={3}
        />
      </g>
    );
  }
  return (
    <rect x={x} y={y} width={width} height={height} fill="#E8E8E8" rx={3} />
  );
}

function CustomTooltipReg({ active, payload }) {
  if (active && payload && payload.length) {
    const val = payload.find((p) => p.dataKey === "value");
    return (
      <div className="bg-gray-900 text-white text-xs px-3 py-1.5 rounded-xl font-manrope-semibold shadow-lg">
        {val?.value ?? 0} user{val?.value !== 1 ? "s" : ""}
      </div>
    );
  }
  return null;
}

function DropdownSelect({ options, value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-[12px] font-manrope-medium text-gray-600 border border-gray-200 rounded-lg px-3 py-1.5 bg-white hover:bg-gray-50 transition-all"
      >
        {value}
        <ChevronDown size={13} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-100 rounded-lg shadow-md z-10 min-w-[110px]">
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              className="block w-full text-left px-3 py-2 text-[12px] font-manrope-regular text-gray-600 hover:bg-gray-50"
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatSkeleton() {
  return (
    <div className="bg-white rounded-2xl p-5 flex-1 flex flex-col justify-between animate-pulse">
      <div className="w-10 h-10 bg-gray-200 rounded-full mb-4" />
      <div>
        <div className="h-2.5 w-20 bg-gray-200 rounded mb-2" />
        <div className="h-6 w-16 bg-gray-200 rounded" />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [regPeriod, setRegPeriod] = useState("Year");
  const [orderPeriod, setOrderPeriod] = useState("This week");

  const authUser = useSelector((s) => s.auth.user);

  const { data: statsRes, isLoading: statsLoading } =
    useGetDashboardStatsQuery();
  const { data: usersRes } = useGetUsersQuery();
  const { data: ordersRes } = useGetOrdersQuery();

  const stats = statsRes?.data;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const users = usersRes?.data || [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const orders = ordersRes?.data || [];

  const regChartData = useMemo(
    () => buildRegData(users, regPeriod),
    [users, regPeriod],
  );
  const orderChartData = useMemo(
    () => buildOrdersData(orders, orderPeriod),
    [orders, orderPeriod],
  );

  const regMax = niceMax(Math.max(...regChartData.map((d) => d.value)));
  const orderMax = niceMax(Math.max(...orderChartData.map((d) => d.value)));

  const adminName = authUser?.name || "Admin";
  const adminEmail = authUser?.email || "";
  const initials = adminName.charAt(0).toUpperCase();

  const statCards = [
    {
      label: "Total users",
      value: statsLoading ? null : (stats?.totalUsers ?? 0).toLocaleString(),
      icon: Users,
    },
    {
      label: "Total orders",
      value: statsLoading ? null : (stats?.totalBookings ?? 0).toLocaleString(),
      icon: ShoppingBag,
    },
    {
      label: "Total earnings",
      value: statsLoading ? null : `$${(stats?.revenue ?? 0).toLocaleString()}`,
      icon: DollarSign,
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      {/* Top Header */}
      <header className="flex items-center justify-between px-8 pt-7 pb-4">
        <div>
          <h1 className="text-[22px] font-manrope-bold text-gray-900">
            Dashboard
          </h1>
          <p className="text-[12px] font-manrope-regular text-gray-400 mt-0.5">
            Your overall dashboard overview. See the statistics, analytics and
            manage them.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <button className="w-9 h-9 rounded-full bg-white border border-gray-100 flex items-center justify-center shadow-sm hover:bg-gray-50 transition-all">
            <Bell size={16} strokeWidth={1.6} className="text-gray-600" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center">
              <span className="text-sm font-manrope-bold text-gray-600">
                {initials}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-[13px] font-manrope-semibold text-gray-900 leading-tight">
                {adminName}
              </span>
              <span className="text-[11px] font-manrope-regular text-gray-400 leading-tight">
                {adminEmail}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 px-8 pb-8">
        {/* Welcome + Stats row */}
        <div className="flex gap-4 mb-6">
          {/* Welcome card */}
          <div className="bg-white rounded-2xl p-6 flex items-center min-w-[220px]">
            <div>
              <h2 className="text-[17px] font-manrope-bold text-gray-900 mb-1">
                Hello {adminName}!
              </h2>
              <p className="text-[11px] font-manrope-regular text-gray-400">
                Track and manage your application from here.
              </p>
              {stats && (
                <div className="mt-3 flex flex-col gap-1">
                  <span className="text-[11px] font-manrope-regular text-gray-400">
                    <span className="font-manrope-semibold text-gray-700">
                      {stats.pending}
                    </span>{" "}
                    pending ·{" "}
                    <span className="font-manrope-semibold text-gray-700">
                      {stats.accepted}
                    </span>{" "}
                    accepted
                  </span>
                  <span className="text-[11px] font-manrope-regular text-gray-400">
                    <span className="font-manrope-semibold text-gray-700">
                      {stats.totalServices}
                    </span>{" "}
                    services active
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Stat cards */}
          <div className="flex gap-4 flex-1">
            {statCards.map(({ label, value, icon: Icon }) =>
              value === null ? (
                <StatSkeleton key={label} />
              ) : (
                <div
                  key={label}
                  className="bg-white rounded-2xl p-5 flex-1 flex flex-col justify-between"
                >
                  <div className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center mb-4">
                    <Icon size={17} className="text-white" strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="text-[11px] font-manrope-regular text-gray-400 mb-0.5">
                      {label}
                    </p>
                    <p className="text-[20px] font-manrope-bold text-gray-900">
                      {value}
                    </p>
                  </div>
                </div>
              ),
            )}
          </div>
        </div>

        {/* Charts row */}
        <div className="flex gap-4">
          {/* Users registration chart */}
          <div className="bg-white rounded-2xl p-6 flex-[1.6]">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-[15px] font-manrope-bold text-gray-900">
                Users registration statistics
              </h3>
              <DropdownSelect
                options={["Year", "Month", "Week"]}
                value={regPeriod}
                onChange={setRegPeriod}
              />
            </div>
            <ResponsiveContainer width="100%" height={270}>
              <ComposedChart
                data={regChartData}
                margin={{ top: 20, right: 5, left: -20, bottom: 0 }}
              >
                <CartesianGrid
                  vertical={false}
                  stroke="#F0F0F0"
                  strokeDasharray="4 4"
                />
                <XAxis
                  dataKey="month"
                  tick={{
                    fontSize: 11,
                    fill: "#9ca3af",
                    fontFamily: "Manrope-Regular",
                  }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{
                    fontSize: 11,
                    fill: "#9ca3af",
                    fontFamily: "Manrope-Regular",
                  }}
                  axisLine={false}
                  tickLine={false}
                  domain={[0, regMax]}
                  ticks={niceTicks(regMax)}
                />
                <Tooltip content={<CustomTooltipReg />} cursor={false} />
                <Bar
                  dataKey="value"
                  shape={<CustomBarShape />}
                  barSize={30}
                  radius={[3, 3, 0, 0]}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#1a1a1a"
                  strokeWidth={2.5}
                  dot={(props) => {
                    const { cx, cy, payload } = props;
                    if (payload.highlight) {
                      return (
                        <circle
                          key={`dot-${payload.month}`}
                          cx={cx}
                          cy={cy}
                          r={5}
                          fill="#1a1a1a"
                          stroke="#fff"
                          strokeWidth={2}
                        />
                      );
                    }
                    return <g key={`dot-${payload.month}`} />;
                  }}
                  activeDot={{ r: 5, fill: "#1a1a1a" }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Orders chart */}
          <div className="bg-white rounded-2xl p-6 flex-1">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-[15px] font-manrope-bold text-gray-900">
                Orders
              </h3>
              <DropdownSelect
                options={["This week", "Last week", "This month"]}
                value={orderPeriod}
                onChange={setOrderPeriod}
              />
            </div>
            <ResponsiveContainer width="100%" height={270}>
              <BarChart
                data={orderChartData}
                margin={{ top: 10, right: 5, left: -20, bottom: 0 }}
                barSize={28}
              >
                <CartesianGrid
                  vertical={false}
                  stroke="#F0F0F0"
                  strokeDasharray="4 4"
                />
                <XAxis
                  dataKey="day"
                  tick={{
                    fontSize: 11,
                    fill: "#9ca3af",
                    fontFamily: "Manrope-Regular",
                  }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{
                    fontSize: 11,
                    fill: "#9ca3af",
                    fontFamily: "Manrope-Regular",
                  }}
                  axisLine={false}
                  tickLine={false}
                  domain={[0, orderMax]}
                  ticks={niceTicks(orderMax)}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={false}
                  contentStyle={{
                    borderRadius: "10px",
                    border: "none",
                    background: "#1a1a1a",
                    color: "#fff",
                    fontSize: "12px",
                    fontFamily: "Manrope-SemiBold",
                  }}
                  itemStyle={{ color: "#fff" }}
                  labelStyle={{ display: "none" }}
                  formatter={(val) => [
                    `${val} order${val !== 1 ? "s" : ""}`,
                    "",
                  ]}
                />
                <Bar dataKey="value" fill="#2d2d2d" radius={[6, 6, 6, 6]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
